import React from "react";
import { cn } from "@/components/utils/utils";
import { ExpiryCountdown } from "@/components/ui/expiry-countdown";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { useLanguage } from "@/contexts/LanguageContext";

interface TravelRequestBadgeProps {
  travelRequest: TravelRequest;
  className?: string;
  variant?: "card" | "table";
}

export const TravelRequestBadge: React.FC<TravelRequestBadgeProps> = ({
  travelRequest,
  className,
  variant = "card"
}) => {
  const { t } = useLanguage();
  
  // Funzione per verificare se la richiesta è nuova (creata negli ultimi 24 ore)
  const isNewRequest = () => {
    const createdAt = new Date(travelRequest.created_at);
    const now = new Date();
    const diffInHours = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    return diffInHours <= 24;
  };

  // Funzione per ottenere il testo del badge status
  const getStatusBadgeText = () => {
    switch (travelRequest.status) {
      case 'assigned':
        return t('travelRequest.statusAssigned');
      case 'completed':
        return t('travelRequest.statusCompleted');
      case 'rejected':
        return t('travelRequest.statusRejected');
      default:
        return travelRequest.status;
    }
  };

  // Funzione per ottenere gli stili del badge status (allineato con ItineraryCard)
  const getStatusBadgeStyles = () => {
    switch (travelRequest.status) {
      case 'assigned':
        return 'bg-gray-100 border border-gray-300 text-gray-800';
      case 'completed':
        return 'bg-green-100 border border-green-300 text-green-800';
      case 'rejected':
        return 'bg-red-100 border border-red-300 text-red-800';
      default:
        return 'bg-gray-100 border border-gray-300 text-gray-800';
    }
  };


  return (
    <div className="flex justify-end flex-wrap gap-1 items-start">
      {travelRequest.status !== 'pending' && (
        <div className={cn(
          "px-3 py-1 rounded-full shadow-sm flex items-center gap-2",
          getStatusBadgeStyles(),
          className
        )}>
          <span className="text-xs font-medium">
            {getStatusBadgeText()}
          </span>
        </div>
      )}
      {!travelRequest.workingOn && (
        <ExpiryCountdown 
          createdAt={travelRequest.created_at} 
          status={travelRequest.status} 
          variant={variant}
          expiryHours={48}
        />
      )}
      {isNewRequest() && (
        <div className="px-3 py-1 bg-blue-100 border border-blue-300 rounded-full shadow-sm flex items-center gap-2">
          <span className="text-xs font-medium text-blue-800">{t('travelRequest.statusNew')}</span>
        </div>
      )}
    </div>
  );
};
